import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaTimes, FaCamera } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import {
  motorsportGalleries,
  resolveGalleryCover,
  resolveGalleryPhotos
} from '../data/motorsportGalleries';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  onClick?: () => void;
  priority?: boolean;
  sizes?: string;
}

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className,
  onClick,
  priority = false,
  sizes = '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const imgRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (priority) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '100px' }
    );
    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [priority]);

  return (
    <div
      ref={imgRef}
      className={`relative w-full h-full flex items-center justify-center ${className || ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {!isInView ? (
        <div className="w-full h-full bg-slate-800 animate-pulse" />
      ) : (
        <>
          {!isLoaded && !hasError && (
            <div className="absolute inset-0 bg-slate-800 animate-pulse" />
          )}
          <img
            src={src}
            alt={alt}
            sizes={sizes}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setIsLoaded(true)}
            onError={() => {
              setHasError(true);
              setIsLoaded(true);
            }}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            draggable={false}
          />
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
              <span className="text-slate-400 text-sm px-2 text-center">Upload pending</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface PhotoGalleryView {
  id: string;
  title: string;
  description: string;
  location: string;
  date: string;
  folder: string;
  coverImage: string;
  photos: string[];
}

const SWIPE_THRESHOLD = 50;

const MotorsportPhotography: React.FC = () => {
  const { theme, themeClasses, accent, isLight } = useTheme();
  const [selectedGallery, setSelectedGallery] = useState<PhotoGalleryView | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const galleries: PhotoGalleryView[] = useMemo(
    () =>
      motorsportGalleries.map((g) => ({
        id: g.id,
        title: g.title,
        description: g.description,
        location: g.location,
        date: g.date,
        folder: g.folder,
        coverImage: resolveGalleryCover(g),
        photos: resolveGalleryPhotos(g)
      })),
    []
  );

  const openPhoto = (gallery: PhotoGalleryView, photoIndex: number) => {
    if (gallery.photos.length === 0) return;
    setSelectedGallery(gallery);
    setCurrentPhotoIndex(photoIndex);
    setIsFullscreen(true);
  };

  const closeGallery = useCallback(() => {
    setIsFullscreen(false);
    setSelectedGallery(null);
    setCurrentPhotoIndex(0);
  }, []);

  const nextPhoto = useCallback(() => {
    if (!selectedGallery) return;
    setCurrentPhotoIndex((prev) =>
      prev === selectedGallery.photos.length - 1 ? 0 : prev + 1
    );
  }, [selectedGallery]);

  const prevPhoto = useCallback(() => {
    if (!selectedGallery) return;
    setCurrentPhotoIndex((prev) =>
      prev === 0 ? selectedGallery.photos.length - 1 : prev - 1
    );
  }, [selectedGallery]);

  // Lock body scroll while lightbox is open (mobile-friendly)
  useEffect(() => {
    if (!isFullscreen) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!selectedGallery || selectedGallery.photos.length === 0) return;
    const preload = (src: string) => {
      const img = new Image();
      img.src = src;
    };
    const next = (currentPhotoIndex + 1) % selectedGallery.photos.length;
    const prev =
      (currentPhotoIndex - 1 + selectedGallery.photos.length) % selectedGallery.photos.length;
    preload(selectedGallery.photos[next]);
    preload(selectedGallery.photos[prev]);
  }, [selectedGallery, currentPhotoIndex]);

  useEffect(() => {
    if (!isFullscreen || !selectedGallery) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextPhoto();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevPhoto();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeGallery();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen, selectedGallery, closeGallery, nextPhoto, prevPhoto]);

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null || touchStartY.current == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;

    // Prefer horizontal swipe; ignore mostly-vertical scrolls
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) nextPhoto();
    else prevPhoto();
  };

  return (
    <div className={`${themeClasses.bg}`}>
      <div className="container mx-auto px-3 sm:px-6 py-8 sm:py-12 max-w-6xl">
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="mb-8 sm:mb-10">
          <div className="text-center sm:text-left">
            <p className={`mb-2 text-xs font-semibold uppercase tracking-[0.2em] ${accent.text}`}>
              Photographer
            </p>
            <h1
              className={`text-3xl sm:text-5xl font-semibold tracking-tight mb-3 ${
                isLight ? 'text-zinc-900' : 'text-white'
              }`}
            >
              Motorsport Photography
            </h1>
            <p className={`text-base sm:text-lg opacity-80 max-w-2xl mx-auto sm:mx-0 px-1 ${themeClasses.text}`}>
              Race weekends, desert stages, and motocross — captured across the UAE and beyond.
            </p>
          </div>
        </motion.div>

        <div className="space-y-12 sm:space-y-16">
          {galleries.map((gallery, galleryIndex) => (
            <motion.section
              key={gallery.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: galleryIndex * 0.08 }}
            >
              <div className="mb-4 sm:mb-5">
                <h2
                  className={`text-xl sm:text-3xl font-semibold mb-2 ${
                    isLight ? 'text-zinc-900' : 'text-white'
                  }`}
                >
                  {gallery.title}
                </h2>
                <div className={`mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm opacity-70 ${themeClasses.text}`}>
                  <span>{gallery.location}</span>
                  <span aria-hidden>·</span>
                  <span>{gallery.date}</span>
                  <span aria-hidden>·</span>
                  <span className="inline-flex items-center gap-1">
                    <FaCamera className="h-3 w-3" />
                    {gallery.photos.length} photos
                  </span>
                </div>
                <p className={`max-w-3xl text-sm sm:text-base leading-relaxed opacity-80 ${themeClasses.text}`}>
                  {gallery.description}
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-4">
                {gallery.photos.map((photo, photoIndex) => (
                  <motion.div
                    key={`${gallery.id}-${photoIndex}`}
                    whileTap={{ scale: 0.98 }}
                    className="relative aspect-square overflow-hidden rounded-lg sm:rounded-xl cursor-pointer group border border-white/10 touch-manipulation"
                    onClick={() => openPhoto(gallery, photoIndex)}
                  >
                    <LazyImage
                      src={photo}
                      alt={`${gallery.title} ${photoIndex + 1}`}
                      priority={photoIndex < 4}
                    />
                    <div className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white sm:bottom-2 sm:left-2 sm:text-xs">
                      {photoIndex + 1}
                    </div>
                    <div className="pointer-events-none absolute inset-0 hidden bg-black/0 transition-colors sm:block sm:group-hover:bg-black/25" />
                    <div className="pointer-events-none absolute inset-0 hidden items-center justify-center opacity-0 transition-opacity sm:flex sm:group-hover:opacity-100">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          theme === 'studio'
                            ? 'bg-teal-700 text-white'
                            : 'bg-teal-400 text-slate-950'
                        }`}
                      >
                        View
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {isFullscreen && selectedGallery && selectedGallery.photos.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black touch-none"
            style={{
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
              paddingLeft: 'env(safe-area-inset-left)',
              paddingRight: 'env(safe-area-inset-right)'
            }}
            onClick={closeGallery}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedGallery.title} photo viewer`}
          >
            {/* Top bar */}
            <div className="absolute inset-x-0 top-0 z-20 flex items-start justify-between gap-3 p-3 sm:p-4 pointer-events-none">
              <div className="min-w-0 max-w-[70%] rounded-lg bg-black/70 px-3 py-2 text-white pointer-events-auto">
                <h3 className="truncate text-sm font-semibold sm:text-base">{selectedGallery.title}</h3>
                <p className="truncate text-xs opacity-80 sm:text-sm">
                  {selectedGallery.location} · {selectedGallery.date}
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  closeGallery();
                }}
                className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-black/70 text-white touch-manipulation"
                aria-label="Close gallery"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Desktop side arrows */}
            {selectedGallery.photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevPhoto();
                  }}
                  className="absolute left-2 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white touch-manipulation sm:left-4 sm:flex"
                  aria-label="Previous photo"
                >
                  <FaChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextPhoto();
                  }}
                  className="absolute right-2 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white touch-manipulation sm:right-4 sm:flex"
                  aria-label="Next photo"
                >
                  <FaChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Image — padded so it clears chrome on phones */}
            <div
              className="absolute inset-0 flex items-center justify-center px-3 pb-24 pt-20 sm:px-12 sm:pb-16 sm:pt-20"
              onClick={(e) => e.stopPropagation()}
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentPhotoIndex}
                  src={selectedGallery.photos[currentPhotoIndex]}
                  alt={`${selectedGallery.title} ${currentPhotoIndex + 1}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02 }}
                  className="max-h-full max-w-full object-contain select-none"
                  draggable={false}
                />
              </AnimatePresence>
            </div>

            {/* Mobile bottom controls + counter */}
            <div
              className="absolute inset-x-0 bottom-0 z-20 flex items-center justify-center gap-3 p-3 sm:p-4 pointer-events-none"
              onClick={(e) => e.stopPropagation()}
            >
              {selectedGallery.photos.length > 1 && (
                <button
                  type="button"
                  onClick={prevPhoto}
                  className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white touch-manipulation sm:hidden"
                  aria-label="Previous photo"
                >
                  <FaChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div className="rounded-full bg-black/70 px-4 py-2 text-sm text-white">
                {currentPhotoIndex + 1} / {selectedGallery.photos.length}
              </div>
              {selectedGallery.photos.length > 1 && (
                <button
                  type="button"
                  onClick={nextPhoto}
                  className="pointer-events-auto flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white touch-manipulation sm:hidden"
                  aria-label="Next photo"
                >
                  <FaChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MotorsportPhotography;
